import type { UploadRawFile } from 'element-plus'

import type { CampaignAdditionalPaymentResource } from '../generated/metapic-api/metapicApi.schemas'
import type {
  ChartMetric,
  MetricKey,
} from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'

import type { AdvertiserListItem } from './advertiser.types'
import type { CampaignStatusEnum } from './campaigns.types'
import type {
  MoneyServer,
  ProcessableImage,
  ProcessableServerImage,
  TargetItem,
} from './common.types'
import type { ParticipantStatus } from './creator.types'
import type {
  SharedBudgetPayload,
  SharedBudgetServer,
  SharedBudgetServerPayload,
} from './shared-budget.types'
import type {
  TrafficSourceCostPayload,
  TrafficSourceCosts,
  TrafficSourceCostServer,
} from './traffic-source-costs.types'

export type CampaignServer = {
  id: number
  campaign_title?: string
  campaign_text?: string
  has_product_seeding: boolean
  additional_payment?: CampaignAdditionalPaymentResource
  image_url?: string
  image?: ProcessableServerImage
  store_id: number
  store_name: string
  store_group_id: number
  store_logo?: ProcessableServerImage
  currency: string
  token: string
  traffic_sources_costs?: TrafficSourceCostServer[]
  type: CampaignTypeEnum
  status: CampaignStatusEnum
  created_at: string
  updated_at: string
  priority: number
  show_for_advertiser: boolean
  name: string
  shared_budget?: SharedBudgetServer
  budget_limit?: number
  consumed_budget?: {
    amount: number
    currency: string
  }
  constraints: {
    per_user_limit: boolean
    current_clicks?: number
    max_clicks?: number
    valid_from?: string
    valid_until?: string
    current_money?: number
  }
  todo: string[]
}

export enum PreregisterItemEnum {
  SocialMediaUser = 'socialMediaUser',
  Email = 'email',
}

export enum CampaignTypeEnum {
  Standard = 'standard',
  UserAccepts = 'user_accept',
  StoreAccepts = 'store_accept',
  Suggestion = 'suggestion',
}

export type PreregisterItem = { type: PreregisterItemEnum; identifier: string; label?: string }
export type TodoList = { key: number; value: string }[]
export type CampaignForm = {
  campaignType: CampaignTypeEnum
  internalName: string
  title: string
  description: string
  advertiserGroup: number
  advertiserList: AdvertiserListItem[]
  hasTargetAllCreators: boolean
  hasPreregisterCreators: boolean
  preregisterItems: PreregisterItem[]
  targets: TargetItem[]
  hasUnmatchedTargets?: boolean
  hasProductSeeding: boolean
  isShownForAdvertiser?: boolean
  hasTodoItems?: boolean
  hasBadge: boolean
  todoList: TodoList
  tempCampaignImage?: Blob | UploadRawFile | undefined
  invoiceAdvertiserDirectly: boolean
  hasCommission: boolean
  hasFixedFee: boolean
  currency: string
  fixedFee?: number
  trafficSourceCosts?: TrafficSourceCosts[]
  validFrom?: string | Date
  validUntil?: string | Date
  hasStartNow: boolean
  hasOpenEnd: boolean
  currentClicks?: number
  maxClicks?: number
  currentBudgetSpent?: number
  budgetLimit?: number
  perUserLimit: boolean
  hasBudget?: boolean
  image?: ProcessableImage
  sharedBudget?: SharedBudgetPayload
  extra?: {
    id: number
    token: string
    status: CampaignStatusEnum
  }
  priority?: Record<number, number | null | undefined>
}

export type TargetsServer = {
  user_ids: number[]
  user_tag_ids: number[]
  store_group_ids: number[]
  client_ids: number[]
  revenue_tier_ids: number[]
  emails: string[]
  social_media_identifiers: string[]
  users: { id: number; username: string | null }[]
  user_tags: { id: number; name: string }[]
  revenue_tiers: { id: number; name: string; client_name: string }[]
  clients: { id: number; name: string }[]
}

export type TargetsPayload = {
  user_ids?: number[]
  user_tag_ids?: number[]
  store_group_ids?: number[]
  emails?: string[]
  social_media_identifiers?: string[]
  client_ids?: number[]
}

type BaseCreationPayload = {
  store_id: number
  store_group_id?: number
  type: CampaignTypeEnum
  campaign_title?: string
  has_product_seeding: boolean
  per_user_limit: boolean
  targets?: TargetsPayload
  campaign_text?: string
  todo?: string[]
  valid_from?: string | null
  valid_until?: string | null
  max_clicks?: number | null
  budget_limit?: number | null
  budget_limit_v2?: MoneyServer | null
  shared_budget_id?: number | null
  shared_budget?: SharedBudgetServerPayload
  traffic_sources_costs?: TrafficSourceCostPayload[]
  name?: string
  show_for_advertiser?: boolean
}

export type CampaignCreationPayload = BaseCreationPayload & {
  additional_payment?: CampaignAdditionalPaymentResource
}

export type CampaignFormSerializedData = {
  campaigns: CampaignCreationPayload[]
  image?: FormData
}

export type CampaignDetailsCreatorsListPayload = {
  id: string
  user_id: number
  display_name: string | null
  status: ParticipantStatus
  links_created: number
  clicks: number
  estimated_gp: number
  estimated_earning_to_cost_ratio: number
  estimated_media_value: { amount: string; currency: string }
  creator_avatar?: ProcessableServerImage
  overrides?: {
    // 0 - campaign's click limit for each user manually removed
    click_limit?: number
    traffic_sources_costs?: TrafficSourceCostServer[]
  }
}[]

export type CampaignOverviewViewMode = 'admin' | 'advertiser'

export type CampaignDetailsCreator = {
  displayName: string | null
  id: string
  userId: number
  linksCount: number
  contentCount: number
  clicksCount: number
  gpRatio: number
  estimatedGp: number
  estimatedMediaValue: number
  avatar?: ProcessableImage
  invitePending?: boolean
  overrides: {
    // if click limit was manually deleted it will be `0`, if it was never set it will be `null`
    clickLimit: number | null
    trafficSourcesCosts: TrafficSourceCosts[]
  }
}

export enum CampaignCreatorsActiveStatusFilter {
  Active = 'active',
  Inactive = 'inactive',
}

export enum CampaignOverviewFilterTargetEnum {
  ContentProduced = 'content_produced',
  EstViews = 'est_views',
  EstUniqueViews = 'est_unique_views',
  EstMediaValue = 'est_media_value',
  Clicks = 'clicks',
  Orders = 'orders',
  OrderValue = 'order_value',
  TotalCost = 'total_cost',
  TotalEarning = 'total_earning',
  GPRatio = 'gpratio',
  EstGP = 'est_gp',
}

export type CampaignStatsPayload = {
  summary: {
    clicks: number
    links_created: number
    orders: number
    order_value: number
    estimated_cost: number
    estimated_earning: number
    estimated_gp: number
    estimated_earning_to_cost_ratio: number
  }
  daily_stats: {
    clicks: number
    estimated_cost: number
    estimated_earning: number
    estimated_gp: number
    estimated_earning_to_cost_ratio: number
    links_created: number
    order_value: number
    orders: number
    period: number
    period_start_date: string
    period_end_date: string
    sources: {
      clicks: number
      estimated_cost: number
      estimated_earning: number
      estimated_gp: number
      estimated_earning_to_cost_ratio: number
      order_value: number
      orders: number
      source: 0 | 1 | 2
    }[]
  }[]
}

export type CampaignMetrics = {
  clicks: number
  linksCreated: number
  orders: number
  orderValue: number
  estimatedCost: number
  estimatedEarnings: number
  estimatedGp: number
  estimatedGpRatio: number
}
export type CampaignSourceMetrics = Omit<CampaignMetrics, 'linksCreated'> & {
  source: 0 | 1 | 2 | 3
}
export type CampaignStats = {
  summary: CampaignMetrics
  dailyStats: (CampaignMetrics & {
    startDate: string
    sources: CampaignSourceMetrics[]
  })[]
}

export type CampaignChartBreakdown = {
  key: string
  value: number
}

export type CampaignChartPoint = {
  date: string
  value: number
  breakdown: CampaignChartBreakdown[]
}

export type CampaignMetricTotals = Partial<Record<MetricKey | ChartMetric, number>>
